import { useState, RefObject, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { StepperWC } from '@pypestream/design-system';
import { cloneDeep } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
  useProStudioStateMatches,
} from '../../../xstate/app.xstate';
import { useNavigate } from 'react-router';
import { LlmCredentialInput } from '../../../../../../api-services/dist/urql';
import { urqlGql } from '@pypestream/api-services/urql';
import { checkForErrors } from '../../../utils';
import { tableStore } from '../../../stores/table-store';
import { useGuidedSetupEnabled } from '../../../hooks/use-closed-stages';
import { BOBCAT_UNRELEASED } from '../../../utils/bobcat-unreleased';
import { guidedSetupStore } from '../../../stores/guided-setup-store';
interface TaskType {
  name: string;
  description?: string;
}

interface UploadedFile {
  id: string;
  name: string;
  size: number;
  type: string;
  file: File;
}

interface SolutionFormState {
  botName: string;
  type: 'supervisor' | 'blank';
  brandName?: string;
  knowledgeAgentContext?: string;
  llmCredentialId?: string;
  maxOutputTokens?: number;
  otherInformation?: string;
  supervisorName?: string;
  tasks?: TaskType[];
  temperature?: number;
  tone?: string;
  topK?: number;
  topP?: number;
}

interface FormSolutionInfoType {
  botName: string;
  type: 'supervisor' | 'blank';
  llmCredentialId?: string;
}

interface FormSolutionConfigType {
  brandName?: string;
  supervisorName?: string;
  tone?: string;
  otherInformation?: string;
  temperature?: number;
  maxOutputTokens?: number;
  topP?: number;
  topK?: number;
}

interface FormSolutionTasksType {
  tasks: TaskType[];
}

interface FormSolutionAgentType {
  tone?: string;
  brandName?: string;
  knowledgeAgentContext?: string;
}

const defaultFormState: SolutionFormState = {
  botName: '',
  type: 'blank',
};

export const useSolutionWizardForm = (swiperRef: RefObject<StepperWC>) => {
  const [solutionFormState, setSolutionFormState] =
    useState<SolutionFormState>(defaultFormState);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const stepBeforeLoadingRef = useRef<number>(0);
  // Project Bobcat: would mark the brand-new solution for auto-publish once it
  // becomes ready — see `tableStore.pendingAutoPublish`'s doc comment. Not part
  // of the October release (`BOBCAT_UNRELEASED`, PSTUDIO-4636), so it is off.
  const autoPublishOnCreateEnabled = BOBCAT_UNRELEASED.autoPublishOnCreate;
  // Project Bobcat: a new solution opens in guided setup when the master,
  // Validate and Observe are all on (`useGuidedSetupEnabled`, PSTUDIO-4583).
  const guidedSetupEnabled = useGuidedSetupEnabled();

  const { credentials, providers, project } = useProStudioCtxSelector(
    (ctx) => ({
      credentials: ctx.credentials,
      providers: ctx.providers,
      project: ctx.project,
    })
  );

  const isBotFlowError = useProStudioStateMatches(
    'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.botFlowError'
  );

  const initialStepForm = useForm<FormSolutionInfoType>({
    defaultValues: {
      botName: '',
      type: 'blank',
    },
  });

  const createAiProviderStepForm = useForm<LlmCredentialInput>({
    defaultValues: {
      apiKey: '',
      name: '',
      model: '',
      llmProviderId: '',
      endpoint: '',
      projectId: '',
    },
  });

  const configStepForm = useForm<FormSolutionConfigType>({
    defaultValues: {
      supervisorName: '',
      tone: '',
      otherInformation: '',
      temperature: 0.2,
      maxOutputTokens: 2048,
      topP: 0.8,
      topK: 0.2,
    },
  });

  const tasksStepForm = useForm<FormSolutionTasksType>({
    defaultValues: {
      tasks: [],
    },
  });

  const agentStepForm = useForm<FormSolutionAgentType>({
    defaultValues: {
      tone: '',
      brandName: '',
      knowledgeAgentContext: '',
    },
  });

  const addFile = useCallback((file: File) => {
    const newFile: UploadedFile = {
      id: uuidv4(),
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    };
    setUploadedFiles((prev) => [...prev, newFile]);
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== fileId));
  }, []);

  const updateFormState = (formData?: Partial<SolutionFormState>) => {
    if (!formData) {
      return;
    }

    setSolutionFormState((prevState) => ({
      ...prevState,
      ...formData,
    }));
  };

  const createSolution = ({
    formData,
    callback,
    sourceStepIndex,
    onBeforeNavigate,
    files,
    empty = false,
  }: {
    callback: () => void;
    /**
     * Project Bobcat — "Create a blank solution": the backend builds its
     * standard rows even for a blank solution, so the new solution's rows are
     * all deleted once they load (see `tableStore.pendingEmptySolution`). An
     * empty solution has nothing to publish and nothing drafted to walk
     * through, so it skips auto-publish and guided setup.
     */
    empty?: boolean;
    /** The files to send to the backend upload, in place of the attached ones. */
    files?: File[];
    formData?: Partial<SolutionFormState>;
    sourceStepIndex?: number;
    /**
     * Project Bobcat — called with the new solution's uniqueId right before
     * `navigate(res)`, for a caller that needs to mark something pending for
     * the solution it's about to land on (see `tableStore.pendingAutoPublish`'s
     * doc comment for the pattern). Optional and additive: the form wizard
     * doesn't pass it, so its own behavior is unchanged. See
     * `use-conversational-create.tsx`'s `create()`.
     */
    onBeforeNavigate?: (uniqueId: string) => void;
  }) => {
    setIsLoading(true);
    const solutionState = {
      ...solutionFormState,
      ...formData,
    };
    if (solutionState.llmCredentialId === '') {
      solutionState.llmCredentialId = undefined;
    }
    const { botName, type: _, ...versionData } = solutionState;

    stepBeforeLoadingRef.current =
      sourceStepIndex ?? swiperRef?.current?.swiper?.activeIndex ?? 0;

    swiperRef?.current?.goToStep(4);

    sendProStudioEvent({
      type: 'createCustomerBot',
      payload: {
        botName,
        files: files ?? uploadedFiles.map((file) => file.file),
        version: {
          ...cloneDeep(versionData),
        },
      },
      cb: (res) => {
        if (!res) {
          setIsLoading(false);
          swiperRef?.current?.goToStep(stepBeforeLoadingRef.current);
          return;
        }

        // `res` is `/${projectId}/${uniqueId}` — read once, shared by both
        // the auto-publish marker below and any caller-supplied
        // `onBeforeNavigate`.
        const uniqueId = res.split('/').filter(Boolean).pop();

        if (uniqueId && empty) {
          tableStore.markPendingEmptySolution(uniqueId);
        }

        if (autoPublishOnCreateEnabled && !empty) {
          // The wizard only ever creates 'main' (never 'survey') bots, so
          // botType is hardcoded rather than read from context, which may
          // not yet reflect this brand-new solution at this exact callback
          // moment.
          if (uniqueId) {
            tableStore.markPendingAutoPublish(uniqueId, 'main');
          }
        }

        // A Knowledge Agent with no documents and no pasted knowledge never
        // gets a corpus provisioned; its RAG lookup is dropped once the new
        // solution's rows load. See `tableStore.pendingSkipKnowledgeCorpus`.
        const skippedKnowledgeBase =
          solutionState.type === 'supervisor' &&
          uploadedFiles.length === 0 &&
          !solutionState.knowledgeAgentContext?.trim();
        if (uniqueId && skippedKnowledgeBase) {
          tableStore.markPendingSkipKnowledgeCorpus(uniqueId);
        }

        // Project Bobcat: group the new solution's Supervisor and Knowledge
        // Agent rows as soon as they load, so Build's canvas opens populated
        // instead of empty until someone groups them by hand in Table View.
        // The conversational flow already marks this; the wizard is the
        // path most solutions are created through. The marker is set only while
        // the master `bobcat-five-stage-nav` is allowed (the mark action checks
        // it), and the load site checks again before writing.
        if (uniqueId && solutionState.type === 'supervisor') {
          tableStore.markPendingAutoAgentGrouping(uniqueId);
        }

        // Project Bobcat: each declared task lands as its own node; group
        // each into a use-case agent once the rows load, so the canvas shows
        // them as agents rather than ungrouped nodes. Runs after the
        // Supervisor/Knowledge grouping (see `pendingUseCaseAgentGrouping`).
        // Set only while the master `bobcat-five-stage-nav` is allowed, and
        // checked again at the load site.
        if (
          uniqueId &&
          solutionState.type === 'supervisor' &&
          solutionState.tasks?.length
        ) {
          tableStore.markPendingUseCaseAgentGrouping(uniqueId);
        }

        if (uniqueId) {
          if (guidedSetupEnabled && !empty) guidedSetupStore.start(uniqueId);
          onBeforeNavigate?.(uniqueId);
        }

        navigate(res);
        callback();
        setIsLoading(false);
        resetFormState();
      },
    });
  };

  const resetFormState = () => {
    setUploadedFiles([]);
    setSolutionFormState(defaultFormState);
    initialStepForm.reset();
    configStepForm.reset();
    tasksStepForm.reset();
    agentStepForm.reset();
    swiperRef?.current?.goToStep(0);
  };

  const handleNextStep = async (
    formData?: Partial<SolutionFormState>,
    isAiProviderStep?: boolean
  ) => {
    updateFormState(formData);
    if (isAiProviderStep) {
      setIsLoading(true);
      const resp = await urqlGql.createCredential({
        credential: {
          ...createAiProviderStepForm.getValues(),
          projectId: project?.id || '',
        },
      });
      setSolutionFormState((prevState) => ({
        ...prevState,
        llmCredentialId: resp.data?.admin_?.createCredential?.id,
      }));
      setIsLoading(false);
      checkForErrors(resp);
    }
    swiperRef?.current?.nextStep();
  };

  const handlePrevStep = () => swiperRef?.current?.backStep();

  const onCloseModal = () => {
    resetFormState();
  };

  return {
    initialStepForm,
    configStepForm,
    tasksStepForm,
    agentStepForm,
    uploadedFiles,
    addFile,
    removeFile,
    updateFormState,
    resetFormState,
    createSolution,
    handleNextStep,
    handlePrevStep,
    createAiProviderStepForm,
    credentials,
    providers,
    onCloseModal,
    isLoading,
    setIsLoading,
    isBotFlowError,
    stepBeforeLoadingRef,
  };
};
